import { useQuery } from "@tanstack/react-query";
import "./status.css";
import { AppHeader } from "../components/AppHeader";
import { LogoBand } from "../components/LogoBand";
import { useSettings } from "../components/useSettings";
import { fetchStatus } from "./api";
import { hrefFor } from "./route";
import {
  OVERALL_LABEL,
  SERVICE_LABEL,
  TONE,
  formatWhen,
  uptimeLine,
} from "./presentation";
import { IncidentCard } from "./IncidentCard";
import type { StatusSummary } from "./types";

/**
 * Public service-status page.
 *
 * Refetches every 60s so a page left open during an incident stays current
 * without the reader thinking to reload — which, during an outage, they will
 * not.
 */
export function StatusPage() {
  useSettings(); // applies dark mode / font scale / underline preferences

  const { data, isPending, isError } = useQuery<StatusSummary>({
    queryKey: ["status"],
    queryFn: fetchStatus,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });

  return (
    <>
      <AppHeader />
      <LogoBand />
      <main className="status-main" id="main">
        <div className="status-head">
          <h1>Service status</h1>
          <p>
            Current availability of LLR Work and Health Support. This page
            updates automatically.
          </p>
        </div>

        {isPending && (
          <div className="status-banner tone-unknown" role="status">
            <span className="status-banner__dot" />
            <span>Checking current status…</span>
          </div>
        )}

        {isError && (
          <div className="status-banner tone-unknown" role="status">
            <span className="status-banner__dot" />
            <span>We cannot show the service status right now</span>
          </div>
        )}

        {data && <StatusBody data={data} />}

        <p className="status-footer">
          If you need urgent help, do not wait for this page to change — use the
          “Get Urgent Help” button above.
        </p>
      </main>
    </>
  );
}

function StatusBody({ data }: { data: StatusSummary }) {
  const resolved = data.recent_incidents.filter((i) => i.status === "resolved");

  return (
    <>
      <div className={`status-banner ${TONE[data.status]}`} role="status">
        <span className="status-banner__dot" />
        <span>{OVERALL_LABEL[data.status] ?? data.status}</span>
      </div>

      <h2 className="status-section-title">Services</h2>
      <div className="status-card">
        {data.services.length === 0 && (
          <div className="status-empty">No services are being reported yet.</div>
        )}
        {data.services.map((s) => (
          <div className="status-row" key={s.key}>
            <div>
              <div className="status-row__name">{s.name}</div>
              {s.description && <div className="status-row__desc">{s.description}</div>}
              <div className="status-row__meta">{uptimeLine(s.uptime_30d, s.last_synced_at)}</div>
            </div>
            <span className={`status-pill ${TONE[s.status]}`}>
              {SERVICE_LABEL[s.status] ?? s.status}
            </span>
          </div>
        ))}
      </div>

      <h2 className="status-section-title">Current issues</h2>
      <div className="status-card">
        {data.active_incidents.length === 0 ? (
          <div className="status-empty">
            There are no known issues. Everything is running normally.
          </div>
        ) : (
          data.active_incidents.map((inc) => (
            <IncidentCard key={inc.reference} incident={inc} linkToDetail />
          ))
        )}
      </div>

      <h2 className="status-section-title">Recently resolved</h2>
      <div className="status-card">
        {resolved.length === 0 ? (
          <div className="status-empty">Nothing resolved recently.</div>
        ) : (
          resolved.map((inc) => (
            <IncidentCard key={inc.reference} incident={inc} linkToDetail collapsed />
          ))
        )}
      </div>

      <p className="status-footer">
        <a href={hrefFor("/status/incidents")}>See all past incidents</a>
        {" · "}
        Last updated {formatWhen(new Date().toISOString())}
      </p>
    </>
  );
}
